import { Item } from '@seen/ui'
import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react'
import { type JSX, memo } from 'react'
import { CarePlanDemographicQuestionnaire } from '#src/components/care-plan/CarePlanDemographicQuestionnaire.tsx'
import { useCarePlanDemographicByUrl } from '#src/components/care-plan/CarePlanDemographicsContext.tsx'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

interface CarePlanQuestionnaireNodeAttributes {
  questionnaireUrl: string | null
}

export function CarePlanQuestionnaireNodeView({
  editor,
  node,
}: NodeViewProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const isEditable = editor.isEditable && !isReadOnly
  const { questionnaireUrl } = node.attrs as CarePlanQuestionnaireNodeAttributes

  return (
    <CarePlanQuestionnaireNodeViewInner
      isEditable={isEditable}
      questionnaireUrl={questionnaireUrl}
    />
  )
}

// Memoized because tiptap re-renders every NodeView on any outer-editor
// transaction, and each editable item here mounts its own nested RichTextEditor
// — so an un-memoized node would rebuild several nested editors per keystroke
// elsewhere in the description.
const CarePlanQuestionnaireNodeViewInner = memo(
  function CarePlanQuestionnaireNodeViewInner({
    isEditable,
    questionnaireUrl,
  }: {
    isEditable: boolean
    questionnaireUrl: string | null
  }): JSX.Element {
    const entry = useCarePlanDemographicByUrl(questionnaireUrl)

    return (
      <NodeViewWrapper as="div">
        <Item variant="outline">
          {entry ? (
            <CarePlanDemographicQuestionnaire
              questionnaire={entry.questionnaire}
              questionnaireResponse={entry.questionnaireResponse}
              readOnly={!isEditable}
            />
          ) : (
            <p className="text-sm text-foreground-secondary">
              This care plan section is unavailable.
            </p>
          )}
        </Item>
      </NodeViewWrapper>
    )
  }
)
